import clsx from 'clsx';
import Link from 'next/link';
import useTranslation from 'next-translate/useTranslation';

// Lib
import { settingsMenu } from '@/lib/platform/settings/menu/menu';

// Hooks
import useUserRole from '@/utils/hooks/user/useUserRole';
import useRouterQuery from '@/hooks/utils/useRouterQuery';
import useAcl from '@/hooks/user/useAcl';

// Components
import Icon from '@/components/base/Icon';

// Types
import type { SettingsSubMenuEntryType } from '@/lib/platform/settings/menu/menu';

type SettingsMenuSubEntryProps = { subEntry: SettingsSubMenuEntryType };

function SettingsMenuSubEntry({ subEntry }: SettingsMenuSubEntryProps) {
    const { t, lang } = useTranslation();
    const { currentRole } = useUserRole();
    const { hasAcl } = useAcl();
    const { deleteAllQueries } = useRouterQuery();
    const urlWithoutParams = deleteAllQueries();

    if (subEntry.acl) {
        if (subEntry.acl.roles) {
            if (!subEntry.acl.roles.includes(currentRole)) {
                return null;
            }
        }

        if (subEntry.acl.permissions_to_hide) {
            if (!hasAcl(subEntry.acl.permissions_to_hide)) {
                return null;
            }
        }
    }

    return (
        <li>
            <Link
                href={subEntry.link.replace('{lang}', lang === 'en' ? '/' : `/${lang}/`)}
                target={subEntry.target_blank ? '_blank' : ''}
                rel={subEntry.target_blank ? 'noopener noreferrer' : null}
                className={clsx(
                    'flex flex-row items-center space-x-2 rounded-md p-2 font-GSemiBold text-xs hover:text-icoBlue',
                    {
                        'bg-icoBlue/10 text-icoBlue': subEntry.link === urlWithoutParams,
                        'text-icoGray-500 dark:text-icoDarkMode-wolf':
                            subEntry.link !== urlWithoutParams
                    }
                )}>
                <Icon icon={subEntry.icon} />
                <span>{t(subEntry.name)}</span>
            </Link>
        </li>
    );
}

type SettingsMenuProps = { className?: string };

export function SettingsMenu({ className }: SettingsMenuProps) {
    return (
        <ul className={clsx('flex flex-col gap-5 pb-6 pt-8', className)}>
            {settingsMenu.map((entry) => (
                <li key={entry.id}>
                    <p
                        data-testid={`settings-menu-${entry.id}`}
                        className="mb-2 px-2 text-8 uppercase tracking-0.5 text-icoBlue">
                        {entry.name}
                    </p>
                    <ul className="flex flex-col space-y-1">
                        {entry.subMenuItems.map((subEntry) => (
                            <SettingsMenuSubEntry key={subEntry.id} subEntry={subEntry} />
                        ))}
                    </ul>
                </li>
            ))}
        </ul>
    );
}
